import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Grid, TextField } from '@material-ui/core';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';

import { catalogApi as api } from 'api/catalog';
import { valuePropsApi } from 'api/catalog/valueProps';
import { catalogCompanySourceMapApi } from 'api/inventory/catalogCompanySourceMap';
import { CustomerGuaranteeDaysByCompanySourceFields } from 'components/catalog/components/CustomerGuaranteeDaysByCompanySourceFields';
import {
    areAllCustomerGuaranteeDaysFilled,
    buildCustomerGuaranteeDaysByCompanySourcePayload,
    buildCustomerGuaranteeDaysPrefill,
    normalizeCustomerGuaranteeDaysEntries,
    shouldSubmitCustomerGuaranteeDaysOnCreate,
    syncCustomerGuaranteeDaysByCompanySource,
} from 'components/catalog/utils/customerGuaranteeDaysByCompanySource';
import { PRICE_POSITIONING_OPTIONS } from 'components/catalog/utils/itemInformationHelpers';
import { FormComponent, Notifications } from 'shared';
import { mapValuePropsToSelectOptions, useValuePropOptionsQuery } from 'shared/hooks/catalog/useValuePropOptionsQuery';
import {
    getFailedValuePropIDs,
    getPendingAttachExternalSku,
    getSavedValuePropIDs,
    getSelectedValuePropIDs,
    getValuePropIDsToAttach,
    mapAssignedValuePropsToFormOptions,
    resolveExternalSkuForValuePropHydration,
    resolveExternalSkuForValueProps,
    shouldAttachValuePropsAfterCreate,
    shouldCompleteCreateItemMasterSubmit,
    shouldSkipItemMasterCreate,
} from 'shared/valueProps/createItemMasterValueProps';

const filter = createFilterOptions();

const DEFAULT_COMPANY_SOURCE_CODE = 'misfits_market';

const yesNoFromBoolean = (value) => (value ? 'Yes' : 'No');

function CreateItemMaster({
    facilities,
    getItemMasters,
    initialValues = {},
    itemMasterRows = [],
    items,
    onSuccess,
    taxonomy,
    tempZones,
}) {
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [externalSku, setExternalSku] = useState('');
    const [externalSkuInputValue, setExternalSkuInputValue] = useState('');
    const [autocompleteKey, setAutocompleteKey] = useState(1);
    const [ingredients, setIngredients] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [companySourceOptions, setCompanySourceOptions] = useState([]);
    const [companySourceIDs, setCompanySourceIDs] = useState([]);
    const [companySourceError, setCompanySourceError] = useState(false);
    const [selectedValueProps, setSelectedValueProps] = useState([]);
    // Set when create succeeds but value-prop attach fails so retry skips create.
    const [pendingValuePropAttach, setPendingValuePropAttach] = useState(null);
    const [customerGuaranteeDaysByCompanySourceID, setCustomerGuaranteeDaysByCompanySourceID] = useState({});
    const [customerGuaranteeDaysError, setCustomerGuaranteeDaysError] = useState(false);
    const [appliedItemMasterCgdKey, setAppliedItemMasterCgdKey] = useState(null);
    const [disableIngredient, setDisableIngredient] = useState(true);
    const externalSkuRef = useRef(externalSku);
    externalSkuRef.current = externalSku;
    const productionMethodOpts = [
        { text: 'Conventional', value: 'CONVENTIONAL' },
        { text: 'Organic', value: 'ORGANIC' },
    ];

    const [taxonomyL1, setTaxonomyL1] = useState('');
    const [taxonomyL2, setTaxonomyL2] = useState('');
    const [barcodeType, setBarcodeType] = useState('');
    const [recTempZone, setRecTempZone] = useState('');
    const [storTempZone, setStorTempZone] = useState('');
    const [shipTempZone, setShipTempZone] = useState('');
    const [sellOnPo, setSellOnPo] = useState('');
    const [baseUnitsPerCase, setBaseUnitsPerCase] = useState();
    const [purchaseCaseUOM, setPurchaseCaseUOM] = useState();
    const [ti, setTI] = useState();
    const [hi, setHI] = useState();
    const [csPallet, setCsPallet] = useState();
    const [purchCasePackSize, setPurchCasePackSize] = useState();
    const [isProduce, setIsProduce] = useState(taxonomyL1 == '1');
    const unitOfMeasureOptions = ['oz', 'ct', 'lb', 'floz', 'pt', 'g'];
    const packagingUnitTypeOptions = ['clamshell', 'pack', 'bunch', 'bag', 'head'];
    const multipackIndividualOptions = ['oz', 'floz'];

    const { data: valuePropCatalog = [] } = useValuePropOptionsQuery();
    const valuePropOptions = mapValuePropsToSelectOptions(valuePropCatalog);

    const getTaxonomyOptions = (row, level) => {
        const parentID = level > 1 ? Number(row[`l${level - 1}TaxonomyID`]) : 0;
        const opts = taxonomy[`l${level}`]
            .filter((item) => item.parentID == parentID)
            .map((item) => ({ text: item.name, value: item.id }));
        if (level > 2) {
            opts.unshift({ text: '', value: null });
        }
        return opts;
    };

    const getAllIngredients = async () => {
        const res = await api.getPropSixtyFiveIngredients();
        if (res.status === true) {
            setIngredients(res.data.rows);
        }
    };

    const getTiers = async () => {
        const res = await api.getItemInformationTiers();
        if (res.status === true) {
            // API returns plain strings: ['A', 'B', 'C']
            setTiers(res.data.rows ?? []);
        }
    };

    const getDefaultCompanySourceIDs = (sources) => {
        const defaultSource = sources.find((source) => source.code === DEFAULT_COMPANY_SOURCE_CODE);
        const id = defaultSource?.id ?? sources[0]?.id;
        return id != null ? [id] : [];
    };

    const getCompanySourceIDsFromInitialValues = () => {
        const sources = initialValues.companySources;
        if (!Array.isArray(sources) || sources.length === 0) {
            return null;
        }
        const ids = sources.map((source) => source.companySourceId ?? source.id).filter((id) => id != null);
        return ids.length > 0 ? ids : null;
    };

    // Keep CGD rows in sync in the same update as company-source selection (avoid mirroring in an effect).
    const updateCompanySourceSelection = useCallback((nextIDs, options = {}) => {
        const { customerGuaranteeDaysByCompanySource, resetValues = false } = options;
        setCompanySourceIDs(nextIDs);
        setCompanySourceError(nextIDs.length === 0);
        setCustomerGuaranteeDaysError(false);

        if (resetValues || customerGuaranteeDaysByCompanySource !== undefined) {
            setCustomerGuaranteeDaysByCompanySourceID(
                buildCustomerGuaranteeDaysPrefill({
                    companySourceIDs: nextIDs,
                    customerGuaranteeDaysByCompanySource,
                }),
            );
            return;
        }

        setCustomerGuaranteeDaysByCompanySourceID((previous) =>
            syncCustomerGuaranteeDaysByCompanySource(previous, nextIDs),
        );
    }, []);

    const getCustomerGuaranteeDaysByCompanySourceForExternalSku = (selectedExternalSku) => {
        // Prefer the selected II autocomplete payload (what the user picked).
        const selectedValue = externalSkuRef.current?.value;
        if (
            selectedValue != null &&
            Number(selectedValue.externalSku) === Number(selectedExternalSku) &&
            selectedValue.customerGuaranteeDaysByCompanySource != null
        ) {
            return selectedValue.customerGuaranteeDaysByCompanySource;
        }

        // Fall back to item-master table rows when they eventually include this SKU.
        const row = itemMasterRows.find((item) => Number(item.externalSku) === Number(selectedExternalSku));
        return row?.customerGuaranteeDaysByCompanySource ?? [];
    };

    // Re-apply display prefill when the selected SKU's CGD source data arrives/changes.
    // Always apply (including empty) so a prior SKU's days cannot linger in locked fields.
    const selectedExternalSkuForCgd = externalSku?.value?.externalSku ?? null;
    const itemMasterCgdEntries =
        selectedExternalSkuForCgd != null
            ? normalizeCustomerGuaranteeDaysEntries(
                  getCustomerGuaranteeDaysByCompanySourceForExternalSku(selectedExternalSkuForCgd),
              )
            : [];
    const itemMasterCgdApplyKey =
        selectedExternalSkuForCgd != null && companySourceIDs.length > 0
            ? `${selectedExternalSkuForCgd}|${companySourceIDs.join(',')}|${itemMasterCgdEntries
                  .map((entry) => `${entry.companySourceID}:${entry.customerGuaranteeDays}`)
                  .join(',')}`
            : null;
    if (itemMasterCgdApplyKey !== appliedItemMasterCgdKey) {
        setAppliedItemMasterCgdKey(itemMasterCgdApplyKey);
        if (itemMasterCgdApplyKey != null) {
            setCustomerGuaranteeDaysByCompanySourceID(
                buildCustomerGuaranteeDaysPrefill({
                    companySourceIDs,
                    customerGuaranteeDaysByCompanySource: itemMasterCgdEntries,
                }),
            );
        }
    }

    const getCompanySources = async () => {
        const res = await catalogCompanySourceMapApi.getCompanySourceOptions();
        if (res.status === true) {
            const sources = res.data?.companySources ?? [];
            setCompanySourceOptions(sources);
            // Don't overwrite IDs already synced from a selected External SKU (options can load later).
            if (externalSkuRef.current?.value?.externalSku != null) {
                return;
            }
            const idsFromInitialValues = getCompanySourceIDsFromInitialValues();
            if (idsFromInitialValues) {
                updateCompanySourceSelection(idsFromInitialValues, {
                    customerGuaranteeDaysByCompanySource: initialValues.customerGuaranteeDaysByCompanySource,
                });
                return;
            }
            updateCompanySourceSelection(getDefaultCompanySourceIDs(sources), { resetValues: true });
        }
    };

    useEffect(() => {
        getAllIngredients();
        getTiers();
        getCompanySources();
    }, []);

    useEffect(() => {
        // taxonomy L1 1 = 'Produce'
        // taxonomy L1 455 = 'Grocery'
        // taxonomy L2 567 = 'Chocolate and Candy'
        setBarcodeType(taxonomyL1 && taxonomyL1 !== '1' ? 'UPC' : '');
        setRecTempZone(taxonomyL1 && taxonomyL1 === '455' ? 'Ambient' : '');
        setStorTempZone(taxonomyL1 && taxonomyL1 === '455' ? 'Ambient (>55)' : '');
        setShipTempZone(() => {
            if (taxonomyL1 && taxonomyL2 && taxonomyL1 === '455' && taxonomyL2 !== '567') {
                return 'Ambient';
            }
            if (taxonomyL1 && taxonomyL1 == '1') {
                return 'Refrigerated';
            }
            return '';
        });
        setPurchaseCaseUOM(purchaseCaseUOM);
    }, [taxonomyL1, taxonomyL2]);

    useEffect(() => {
        if (taxonomyL1 && taxonomyL1 === '455') {
            setPurchCasePackSize(baseUnitsPerCase);
        }
    }, [baseUnitsPerCase]);

    useEffect(() => {
        if (Object.keys(initialValues).length > 0) {
            setBarcodeType(initialValues.barcodeType);
            setShipTempZone(initialValues.shippingTempZone || '');
            setStorTempZone(initialValues.storageTempZone || '');
            setRecTempZone(initialValues.receivingTempZone || '');
            setBaseUnitsPerCase(initialValues.baseUnitsPerCase);
            setPurchCasePackSize(initialValues.purchasingCasePackSize);
            setSellOnPo(initialValues.sellOnPoQty === true ? 'Yes' : 'No');
            setTI(initialValues.ti);
            setHI(initialValues.hi);
            if (initialValues.ti && initialValues.hi) {
                setCsPallet(Number(initialValues.ti) * Number(initialValues.hi));
            } else {
                // Not sure if this is possible, but its a catch all and maintains the previous logic in the unlikely case that ti and hi are not set
                setCsPallet(initialValues.csPallet);
            }
            setTaxonomyL1(initialValues.l1TaxonomyID);

            // Ensures we update the brand field if the taxonomy is produce
            setIsProduce(initialValues.l1TaxonomyID == '1');

            const companySourceIDsFromRow = getCompanySourceIDsFromInitialValues();
            if (companySourceIDsFromRow) {
                updateCompanySourceSelection(companySourceIDsFromRow, {
                    customerGuaranteeDaysByCompanySource: initialValues.customerGuaranteeDaysByCompanySource,
                });
            }
        }
    }, [initialValues]);

    useEffect(() => {
        setTaxonomyL1(externalSku?.value?.l1TaxonomyID);

        const syncCompanySourcesForExternalSku = async () => {
            const selectedExternalSku = externalSku?.value?.externalSku;
            if (selectedExternalSku == null) {
                return;
            }

            const applyCompanySourceIDs = (ids) => {
                // Ignore stale responses if the SKU was cleared or changed while the request was in flight.
                if (externalSkuRef.current?.value?.externalSku !== selectedExternalSku) {
                    return;
                }
                // Always pass an array (possibly empty) so previous SKU CGD values are cleared.
                updateCompanySourceSelection(ids, {
                    customerGuaranteeDaysByCompanySource:
                        getCustomerGuaranteeDaysByCompanySourceForExternalSku(selectedExternalSku) ?? [],
                });
            };

            // Prefer company sources already present on the selected item information row.
            const fromItem = externalSku.value.companySources;
            if (Array.isArray(fromItem) && fromItem.length > 0) {
                const ids = fromItem.map((source) => source.companySourceId ?? source.id).filter((id) => id != null);
                if (ids.length > 0) {
                    applyCompanySourceIDs(ids);
                    return;
                }
            }

            const res = await catalogCompanySourceMapApi.getCompanySourceOptionsByExternalSku(selectedExternalSku);
            if (res.status === true) {
                const sources = res.data?.companySources ?? [];
                applyCompanySourceIDs(sources.map((source) => source.id).filter((id) => id != null));
            }
        };

        syncCompanySourcesForExternalSku();
    }, [externalSku]);

    // Duplicate open: hydrate once from the source row. Do not re-run on free-solo SKU changes
    // or user edits to value props will be overwritten by initialValues.externalSku.
    useEffect(() => {
        const skuToLoad = resolveExternalSkuForValuePropHydration({
            selectedExternalSku: null,
            initialExternalSku: initialValues.externalSku,
        });
        if (skuToLoad == null) {
            return;
        }

        const syncValueProps = async () => {
            if (externalSkuRef.current?.value?.externalSku != null) {
                return;
            }
            const res = await valuePropsApi.getValuePropsForExternalSku(skuToLoad);
            if (res.status !== true) {
                return;
            }
            if (externalSkuRef.current?.value?.externalSku != null) {
                return;
            }
            setSelectedValueProps(mapAssignedValuePropsToFormOptions(res.data?.valueProps ?? []));
        };

        syncValueProps();
    }, [initialValues.externalSku]);

    // Existing external SKU select: sync that II's value props. No initialValues fallback.
    useEffect(() => {
        const selectedAtStart = externalSku?.value?.externalSku;
        const skuToLoad = resolveExternalSkuForValuePropHydration({
            selectedExternalSku: selectedAtStart,
            initialExternalSku: null,
        });
        if (skuToLoad == null) {
            return;
        }

        const syncValueProps = async () => {
            const res = await valuePropsApi.getValuePropsForExternalSku(skuToLoad);
            if (res.status !== true) {
                return;
            }
            if (Number(externalSkuRef.current?.value?.externalSku) !== skuToLoad) {
                return;
            }
            setSelectedValueProps(mapAssignedValuePropsToFormOptions(res.data?.valueProps ?? []));
        };

        syncValueProps();
    }, [externalSku?.value?.externalSku]);

    const formFieldsRearranged = [
        {
            name: 'itemNameInternal',
            inputElement: 'textField',
            label: 'Internal Name',
            gridValue: 6,
            inputProps: { required: true },
        },
        {
            name: 'brand',
            inputElement: 'textField',
            label: 'Brand',
            gridValue: 6,

            // value is used if you want to disable the form field and not let the user change it
            value: externalSku ? externalSku.value.brand : undefined,
            inputProps: externalSku ? { disabled: true } : { required: !isProduce },

            // if no existing external sku, and an initial value was passed in, set the initial value
            initialValue: !externalSku && initialValues.hasOwnProperty('brand') ? initialValues.brand : undefined,
        },
        {
            name: 'l1TaxonomyID',
            inputElement: 'select',
            label: 'Item Category One',
            gridValue: 6,

            // if existing external sku
            value: externalSku ? externalSku.value.l1TaxonomyID : undefined,
            inputProps: externalSku
                ? {
                      opts: (formData) => taxonomy.l1.map((item) => ({ text: item.name, value: item.id })),
                      disabled: true,
                  }
                : {
                      required: true,
                      opts: (formData) => getTaxonomyOptions(formData, 1),
                      onChange: (e) => {
                          setTaxonomyL1(e.target.value);
                          setIsProduce(e.target.value == '1');
                      },
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('l1TaxonomyID') ? initialValues.l1TaxonomyID : undefined,
        },
        {
            name: 'l2TaxonomyID',
            inputElement: 'select',
            label: 'Item Category Two',
            gridValue: 6,
            dependencies: ['l1TaxonomyID'],

            value: externalSku ? externalSku.value.l2TaxonomyID : undefined,
            inputProps: externalSku
                ? {
                      opts: (formData) => taxonomy.l2.map((item) => ({ text: item.name, value: item.id })),
                      disabled: true,
                  }
                : {
                      required: true,
                      opts: (formData) => getTaxonomyOptions(formData, 2),
                      onChange: (e) => setTaxonomyL2(e.target.value),
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('l2TaxonomyID') ? initialValues.l2TaxonomyID : undefined,
        },
        {
            name: 'l3TaxonomyID',
            inputElement: 'select',
            label: 'Item Category Three',
            gridValue: 6,
            dependencies: ['l1TaxonomyID', 'l2TaxonomyID'],

            value: externalSku ? externalSku.value.l3TaxonomyID : undefined,
            inputProps: externalSku
                ? {
                      opts: (formData) => taxonomy.l3.map((item) => ({ text: item.name, value: item.id })),
                      disabled: true,
                  }
                : { required: true, opts: (formData) => getTaxonomyOptions(formData, 3) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('l3TaxonomyID') ? initialValues.l3TaxonomyID : undefined,
        },
        {
            name: 'l4TaxonomyID',
            inputElement: 'select',
            label: 'Item Category Four',
            gridValue: 6,
            dependencies: ['l1TaxonomyID', 'l2TaxonomyID', 'l3TaxonomyID'],

            value: externalSku ? externalSku.value.l4TaxonomyID : undefined,
            inputProps: externalSku
                ? {
                      opts: (formData) => {
                          const taxOpts = taxonomy.l4.map((item) => ({ text: item.name, value: item.id }));
                          taxOpts.unshift({ text: '', value: null });
                          return taxOpts;
                      },
                      disabled: true,
                  }
                : {
                      opts: (formData) => getTaxonomyOptions(formData, 4),
                      required: !!isProduce,
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('l4TaxonomyID') ? initialValues.l4TaxonomyID : undefined,
        },
        {
            inputElement: () => <h3 style={{ textAlign: 'left' }}>SKU Attributes</h3>,
        },
        {
            name: 'barcode',
            inputElement: 'textField',
            label: 'Barcode',
            gridValue: 4,
            inputProps: { type: 'number', maxLength: 14 },
        },
        {
            name: 'barcodeType',
            inputElement: 'select',
            label: 'Barcode Type',
            gridValue: 4,
            value: externalSku ? '' : barcodeType,
            initialValue: initialValues.barcodeType || '',
            dependencies: ['barcode'],
            inputProps: { opts: ['UPC', 'PLU', 'EAN'], onChange: (e) => setBarcodeType(e.target.value) },
        },
        {
            name: 'msrpPrice',
            inputElement: 'textField',
            label: 'Base Price',
            gridValue: 4,

            value: externalSku ? externalSku.value.msrpPrice : undefined,
            inputProps: externalSku ? { disabled: true } : { type: 'number', max: 300, required: true },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('msrpPrice') ? initialValues.msrpPrice : undefined,
        },
        {
            name: 'prop65Warning',
            inputElement: 'checkbox',
            label: 'Prop65 Warning',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65Warning : undefined,
            inputProps: externalSku ? { disabled: true } : undefined,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('prop65Warning') ? initialValues.prop65Warning : undefined,
        },
        {
            name: 'prop65WarningCategoryAlcohol',
            inputElement: 'checkbox',
            label: 'Prop65 Alcohol Flag',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65WarningCategoryAlcohol : undefined,
            inputProps: externalSku ? { disabled: true } : undefined,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('prop65WarningCategoryAlcohol')
                    ? initialValues.prop65WarningCategoryAlcohol
                    : undefined,
        },
        {
            name: 'prop65WarningCategoryCanned',
            inputElement: 'checkbox',
            label: 'Prop65 Canned Flag',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65WarningCategoryCanned : undefined,
            inputProps: externalSku ? { disabled: true } : undefined,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('prop65WarningCategoryCanned')
                    ? initialValues.prop65WarningCategoryCanned
                    : undefined,
        },
        {
            name: 'prop65WarningCategoryFood',
            inputElement: 'checkbox',
            label: 'Prop65 Food Flag',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65WarningCategoryFood : undefined,
            inputProps: externalSku ? { disabled: true } : { onChange: (e) => setDisableIngredient(!e.target.checked) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('prop65WarningCategoryFood')
                    ? initialValues.prop65WarningCategoryFood
                    : undefined,
        },
        {
            name: 'prop65WarningCategoryNonFood',
            inputElement: 'checkbox',
            label: 'Prop65 Non-Food Flag',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65WarningCategoryNonFood : undefined,
            inputProps: externalSku ? { disabled: true } : undefined,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('prop65WarningCategoryNonFood')
                    ? initialValues.prop65WarningCategoryNonFood
                    : undefined,
        },
        {
            name: 'prop65Ingredient',
            inputElement: 'select',
            label: 'Prop65 Ingredient',
            gridValue: 2,

            value: externalSku ? externalSku.value.prop65Ingredient : undefined,
            inputProps: externalSku
                ? {
                      opts: (formData) => ingredients.map((item) => ({ text: item.name, value: item.id })),
                      disabled: true,
                  }
                : {
                      disabled: disableIngredient,
                      opts: (formData) => ingredients.map((item) => ({ text: item.name, value: item.id })),
                  },
        },
        {
            name: 'portionCountMischief',
            inputElement: 'textField',
            label: 'Portion Count',
            gridValue: 3,
            initialValue: initialValues.portionCountMischief || undefined,
            inputProps: { required: true, type: 'number' },
        },
        {
            name: 'replenishFacilities',
            inputElement: 'autocomplete',
            label: 'Replenishment',
            gridValue: 3,
            inputProps: { opts: facilities.map((row) => row.name), multiple: true },
        },
        {
            name: 'sellOnPoQty',
            inputElement: 'select',
            label: 'Sell On Po Qty',
            gridValue: 3,

            value: !!externalSku && externalSku.value.sellOnPoQty ? 'Yes' : sellOnPo,
            inputProps: externalSku
                ? { disabled: true, opts: ['Yes', 'No'] }
                : { opts: ['Yes', 'No'], onChange: (e) => setSellOnPo(e.target.value) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('sellOnPoQty') && initialValues.sellOnPoQty === true
                    ? 'Yes'
                    : 'No',
        },
        {
            name: 'productionMethod',
            inputElement: 'select',
            label: 'OG/CV',
            gridValue: 3,

            value: externalSku ? externalSku.value.productionMethod : undefined,
            inputProps: externalSku
                ? { disabled: true, opts: productionMethodOpts }
                : { opts: productionMethodOpts, required: !!isProduce },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('productionMethod') ? initialValues.productionMethod : '',
        },
        {
            name: 'dbiOnly',
            label: 'Fulfillable by DBI Only',
            inputElement: 'select',
            gridValue: 2,

            value: !!externalSku && externalSku.value.dbiOnly ? 'Yes' : undefined,
            inputProps: externalSku ? { disabled: true, opts: ['Yes', 'No'] } : { opts: ['Yes', 'No'] },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('dbiOnly') && initialValues.dbiOnly === true
                    ? 'Yes'
                    : 'No',
        },
        {
            name: 'isBulkItem',
            label: 'Bulk Item?',
            inputElement: 'select',
            gridValue: 2,

            value: externalSku ? yesNoFromBoolean(externalSku.value.isBulkItem) : undefined,
            inputProps: externalSku ? { disabled: true, opts: ['Yes', 'No'] } : { opts: ['Yes', 'No'] },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('isBulkItem') && initialValues.isBulkItem === true
                    ? 'Yes'
                    : 'No',
        },
        {
            name: 'tier',
            inputElement: 'select',
            label: 'Tier',
            gridValue: 2,

            value: externalSku ? externalSku.value.tier : undefined,
            inputProps: externalSku
                ? {
                      // Same string opts FormComponent accepts (API returns 'A'/'B'/'C')
                      opts: (formData) => tiers,
                      disabled: true,
                  }
                : {
                      opts: (formData) => tiers,
                  },
            initialValue: !externalSku && initialValues.hasOwnProperty('tier') ? initialValues.tier : undefined,
        },
        {
            name: 'valueProps',
            gridValue: 6,
            // Controlled outside FormComponent formData so selections persist when hydrating
            // from duplicate / existing external SKU (autocomplete does not honor item.value).
            inputElement: () => (
                <Autocomplete
                    id="ValueProps_autocomplete"
                    multiple
                    disabled={!!externalSku?.value}
                    options={valuePropOptions}
                    getOptionLabel={(option) => option.text || ''}
                    getOptionSelected={(option, value) => option.value === value.value}
                    value={selectedValueProps}
                    onChange={(event, selected) => {
                        setSelectedValueProps(selected || []);
                    }}
                    renderInput={(params) => <TextField {...params} label="Value Props" />}
                />
            ),
        },
        {
            name: 'pricePositioning',
            inputElement: 'select',
            label: 'Price Positioning',
            gridValue: 2,
            value: externalSku ? externalSku.value.pricePositioning : undefined,
            inputProps: externalSku
                ? {
                      opts: PRICE_POSITIONING_OPTIONS,
                      disabled: true,
                  }
                : {
                      opts: PRICE_POSITIONING_OPTIONS,
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('pricePositioning')
                    ? initialValues.pricePositioning
                    : undefined,
        },
        {
            name: 'innovation',
            label: 'Innovation',
            inputElement: 'select',
            gridValue: 2,
            value: externalSku ? yesNoFromBoolean(externalSku.value.innovation) : undefined,
            inputProps: externalSku ? { disabled: true, opts: ['Yes', 'No'] } : { opts: ['Yes', 'No'] },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('innovation') && initialValues.innovation === true
                    ? 'Yes'
                    : 'No',
        },
        {
            inputElement: () => <div />,
        },
        {
            inputElement: () => <h3 style={{ textAlign: 'left' }}>Unit/Case Specs</h3>,
        },
        {
            name: 'ti',
            inputElement: 'textField',
            label: 'TI',
            gridValue: 4,
            initialValue: initialValues.ti || undefined,
            inputProps: {
                type: 'number',
                required: true,
                onChange: (e) => {
                    setTI(Number(e.target.value));
                    if (hi) {
                        setCsPallet(Number(hi) * Number(e.target.value));
                    }
                },
            },
        },
        {
            name: 'hi',
            inputElement: 'textField',
            label: 'HI',
            gridValue: 4,
            initialValue: initialValues.hi || undefined,
            inputProps: {
                type: 'number',
                required: true,
                onChange: (e) => {
                    setHI(Number(e.target.value));
                    if (ti) {
                        setCsPallet(Number(ti) * Number(e.target.value));
                    }
                },
            },
        },
        {
            name: 'baseUnitSize',
            inputElement: 'textField',
            label: 'Sellable Unit Size',
            gridValue: 4,

            value: externalSku ? externalSku.value.baseUnitSize : undefined,
            inputProps: externalSku ? { disabled: true } : { required: true, type: 'number', step: '.01', min: 0 },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('baseUnitSize') ? initialValues.baseUnitSize : undefined,
        },
        {
            name: 'baseUnitUOM',
            inputElement: 'select',
            label: 'Sellable Unit UOM',
            gridValue: 4,

            value: externalSku ? externalSku.value.baseUnitUOM : undefined,
            inputProps: externalSku
                ? { disabled: true, opts: unitOfMeasureOptions }
                : { required: true, opts: unitOfMeasureOptions },
            initialValue: !externalSku && initialValues.hasOwnProperty('baseUnitUOM') ? initialValues.baseUnitUOM : '',
        },
        {
            name: 'multipackIndividualUnitSize',
            inputElement: 'textField',
            label: 'MP Individual Unit Size',
            gridValue: 4,

            value: externalSku ? externalSku.value.multipackIndividualUnitSize : undefined,
            inputProps: externalSku ? { disabled: true } : { required: false, type: 'number', step: '.01', min: 0 },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('multipackIndividualUnitSize')
                    ? initialValues.multipackIndividualUnitSize
                    : undefined,
        },
        {
            name: 'multipackIndividualUOM',
            inputElement: 'select',
            label: 'MP Individual Unit UOM',
            gridValue: 4,

            value: externalSku ? externalSku.value.multipackIndividualUOM : undefined,
            inputProps: externalSku
                ? { opts: multipackIndividualOptions, disabled: true }
                : {
                      required: false,
                      opts: [''].concat(multipackIndividualOptions),
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('multipackIndividualUOM')
                    ? initialValues.multipackIndividualUOM
                    : undefined,
        },
        {
            name: 'packagingUnitSize',
            inputElement: 'textField',
            label: 'Packaging Unit Size',
            gridValue: 6,

            value: externalSku ? externalSku.value.packagingUnitSize : undefined,
            inputProps: externalSku ? { disabled: true } : { required: false, type: 'number', step: '.01', min: 0 },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('packagingUnitSize')
                    ? initialValues.packagingUnitSize
                    : undefined,
        },
        {
            name: 'packagingUnitType',
            inputElement: 'select',
            label: 'Packaging Unit Type',
            gridValue: 6,

            value: externalSku ? externalSku.value.packagingUnitType : undefined,
            inputProps: externalSku
                ? { opts: packagingUnitTypeOptions, disabled: true }
                : {
                      required: false,
                      opts: [''].concat(packagingUnitTypeOptions),
                  },
            initialValue:
                !externalSku &&
                initialValues.hasOwnProperty('packagingUnitType') &&
                initialValues.packagingUnitType !== null
                    ? initialValues.packagingUnitType
                    : undefined,
        },
        {
            name: 'baseUnitsPerCase',
            inputElement: 'textField',
            label: 'Sellable Units Per Case',
            gridValue: 4,
            value: externalSku ? '' : baseUnitsPerCase,
            initialValue: initialValues.baseUnitsPerCase || undefined,
            inputProps: {
                required: true,
                type: 'number',
                onChange: (e) => {
                    setBaseUnitsPerCase(e.target.value);
                    if (!isProduce) {
                        setPurchCasePackSize(e.target.value);
                    }
                },
            },
        },
        {
            name: 'purchasingCasePackSize',
            inputElement: 'textField',
            label: 'Purchasing Case Pack Size',
            gridValue: 6,
            value: isProduce ? '' : baseUnitsPerCase,
            initialValue: initialValues.hasOwnProperty('purchasingCasePackSize')
                ? initialValues.purchasingCasePackSize
                : undefined,
            inputProps: isProduce
                ? { required: true, type: 'number', onChange: (e) => setPurchCasePackSize(e.target.value) }
                : { disabled: true },
        },
        {
            name: 'purchasingCaseUOM',
            inputElement: 'select',
            label: 'Purchasing Case UOM',
            gridValue: 6,
            value: purchaseCaseUOM || undefined,
            initialValue: initialValues.hasOwnProperty('purchasingCaseUOM')
                ? initialValues.purchasingCaseUOM
                : undefined,
            inputProps: {
                required: true,
                opts: unitOfMeasureOptions,
                onChange: (e) => setPurchaseCaseUOM(e.target.value),
            },
        },
        {
            name: 'cubeLevelPerBaseUnit',
            inputElement: 'textField',
            label: 'Cube Level Per Sellable Unit',
            gridValue: 4,

            value: externalSku ? externalSku.value.cubeLevelPerBaseUnit : undefined,
            inputProps: externalSku ? { disabled: true } : { required: true, type: 'number', max: 100 },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('cubeLevelPerBaseUnit')
                    ? initialValues.cubeLevelPerBaseUnit
                    : undefined,
        },
        {
            name: 'csPallet',
            inputElement: 'textField',
            label: 'Cases per pallet (cs/pallet)',
            gridValue: 4,
            value: ti && hi ? csPallet : undefined,
            initialValue: !externalSku && initialValues.hasOwnProperty('csPallet') ? initialValues.csPallet : undefined,
            inputProps: { disabled: true, required: true, type: 'number' },
        },
        {
            name: 'lbCs',
            inputElement: 'textField',
            label: 'Pounds per case (lbs/cs)',
            gridValue: 4,
            initialValue: !externalSku && initialValues.hasOwnProperty('lbCs') ? initialValues.lbCs : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'lengthInCs',
            inputElement: 'textField',
            label: 'Case length (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('lengthInCs') ? initialValues.lengthInCs : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'widthInCs',
            inputElement: 'textField',
            label: 'Case width (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('widthInCs') ? initialValues.widthInCs : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'heightInCs',
            inputElement: 'textField',
            label: 'Case height (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('heightInCs') ? initialValues.heightInCs : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'lengthInUnit',
            inputElement: 'textField',
            label: 'Unit length (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('lengthInUnit') ? initialValues.lengthInUnit : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'widthInUnit',
            inputElement: 'textField',
            label: 'Unit width (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('widthInUnit') ? initialValues.widthInUnit : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'heightInUnit',
            inputElement: 'textField',
            label: 'Unit height (in)',
            gridValue: 4,
            initialValue:
                !externalSku && initialValues.hasOwnProperty('heightInUnit') ? initialValues.heightInUnit : undefined,
            inputProps: { required: true, type: 'number', step: '.01', min: 0 },
        },
        {
            name: 'ozUnit',
            inputElement: 'textField',
            label: 'Unit weight (oz)',
            gridValue: 4,
            initialValue: !externalSku && initialValues.hasOwnProperty('ozUnit') ? initialValues.ozUnit : undefined,
            inputProps: { type: 'number', step: '.01', min: 0 },
        },
        {
            inputElement: () => <h3 style={{ textAlign: 'left' }}>Expiration &amp; Temp</h3>,
        },
        {
            name: 'vendorGuaranteedDaysUponDelivery',
            inputElement: 'textField',
            label: 'Vendor Guaranteed Days Upon Delivery',
            gridValue: 4,
            initialValue: initialValues.vendorGuaranteedDaysUponDelivery || undefined,
            inputProps: { required: true, type: 'number' },
        },
        {
            name: 'customerGuaranteeDaysByCompanySource',
            gridValue: 8,
            inputElement: () => (
                <CustomerGuaranteeDaysByCompanySourceFields
                    companySourceIDs={companySourceIDs}
                    companySourceOptions={companySourceOptions}
                    valuesByCompanySourceID={customerGuaranteeDaysByCompanySourceID}
                    onChange={(next) => {
                        setCustomerGuaranteeDaysByCompanySourceID(next);
                        setCustomerGuaranteeDaysError(false);
                    }}
                    // Locked when an existing II SKU is selected — CGD is owned by item information.
                    disabled={!!externalSku?.value}
                    showError={customerGuaranteeDaysError}
                />
            ),
        },
        {
            name: 'receivingTempZone',
            inputElement: 'select',
            label: 'Receiving Temp Zone',
            gridValue: 3,

            value: externalSku ? externalSku.value.receivingTempZone : recTempZone,
            inputProps: externalSku
                ? { disabled: true, opts: tempZones.receiving }
                : { required: true, opts: tempZones.receiving, onChange: (e) => setRecTempZone(e.target.value) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('receivingTempZone')
                    ? initialValues.receivingTempZone
                    : undefined,
        },
        {
            name: 'storageTempZone',
            inputElement: 'select',
            label: 'Storage Temp Zone',
            gridValue: 3,

            value: externalSku ? externalSku.value.storageTempZone : storTempZone,
            inputProps: externalSku
                ? { disabled: true, opts: tempZones.storage }
                : { required: true, opts: tempZones.storage, onChange: (e) => setStorTempZone(e.target.value) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('storageTempZone')
                    ? initialValues.storageTempZone
                    : undefined,
        },
        {
            name: 'shippingTempZone',
            inputElement: 'select',
            label: 'Carrier Shipping Temp Zone',
            gridValue: 3,

            value: externalSku ? externalSku.value.shippingTempZone : shipTempZone,
            inputProps: externalSku
                ? { disabled: true, opts: tempZones.shipping }
                : { required: true, opts: tempZones.shipping, onChange: (e) => setShipTempZone(e.target.value) },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('shippingTempZone')
                    ? initialValues.shippingTempZone
                    : undefined,
        },
        {
            name: 'dbiShippingTempZone',
            inputElement: 'select',
            label: 'DBI Shipping Temp Zone',
            gridValue: 3,

            value: externalSku ? externalSku.value.dbiShippingTempZone : undefined,
            inputProps: externalSku
                ? { disabled: true, opts: tempZones.dbiShipping }
                : { required: true, opts: tempZones.dbiShipping },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('dbiShippingTempZone')
                    ? initialValues.dbiShippingTempZone
                    : undefined,
        },
        {
            name: 'inboundShippingTempZone',
            inputElement: 'select',
            label: 'Inbound Shipping Temp Zone',
            gridValue: 3,

            value: externalSku ? externalSku.value.inboundShippingTempZone : undefined,
            inputProps: externalSku
                ? { disabled: true, opts: tempZones.inboundShipping }
                : { opts: tempZones.inboundShipping },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('inboundShippingTempZone')
                    ? initialValues.inboundShippingTempZone
                    : undefined,
        },
        // CUBE LEVEL PER BASE UNIT 4 - THIS IS ON HERE TWICE
        {
            name: 'expirationType',
            inputElement: 'select',
            label: 'SKU Date Type',
            gridValue: 4,

            value: externalSku ? externalSku.value.expirationType : undefined,
            inputProps: externalSku
                ? {
                      disabled: true,
                      opts: ['NO_EXPIRATION_DATE', 'EXPIRATION_DATE', 'PRODUCTION_DATE', 'DAYS_AFTER_RECEIPT'],
                      disableUnderline: true,
                  }
                : {
                      required: true,
                      opts: ['NO_EXPIRATION_DATE', 'EXPIRATION_DATE', 'PRODUCTION_DATE', 'DAYS_AFTER_RECEIPT'],
                  },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('expirationType')
                    ? initialValues.expirationType
                    : undefined,
        },
        {
            name: 'expireLifeDays',
            inputElement: 'textField',
            label: 'Expire Life Days',
            gridValue: 4,

            value: externalSku ? externalSku.value.expireLifeDays : undefined,
            inputProps: externalSku ? { disabled: true, type: 'number' } : { required: false, type: 'number' },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('expireLifeDays')
                    ? initialValues.expireLifeDays
                    : undefined,
        },
        {
            name: 'daysAfterThaw',
            inputElement: 'textField',
            label: 'Days After Thaw',
            gridValue: 6,
            dependencies: ['storageTempZone'],
            display: (formData) => formData.storageTempZone === 'Frozen (0-31)',

            value: externalSku ? externalSku.value.daysAfterThaw : undefined,
            inputProps: externalSku ? { disabled: true, type: 'number' } : { type: 'number' },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('daysAfterThaw') ? initialValues.daysAfterThaw : undefined,
        },
        {
            name: 'daysAfterOpening',
            inputElement: 'textField',
            label: 'Days After Opening',
            gridValue: 6,
            dependencies: ['storageTempZone'],
            display: (formData) => formData.storageTempZone === 'Frozen (0-31)',

            value: externalSku ? externalSku.value.daysAfterOpening : undefined,
            inputProps: externalSku ? { disabled: true, type: 'number' } : { type: 'number' },
            initialValue:
                !externalSku && initialValues.hasOwnProperty('daysAfterOpening')
                    ? initialValues.daysAfterOpening
                    : undefined,
        },
    ];

    const createItemMaster = async (formData) => {
        if (!companySourceIDs.length) {
            setCompanySourceError(true);
            setNotification({ text: 'At least one company source is required', level: 'error' });
            return false;
        }
        setCompanySourceError(false);

        const isExistingItemInformation = Boolean(externalSku?.value);
        const shouldSubmitCgd = shouldSubmitCustomerGuaranteeDaysOnCreate({
            isExistingItemInformation,
        });

        // Existing-SKU (variant) create must not require/send CGD — attrs stay on the shared II.
        if (
            shouldSubmitCgd &&
            !areAllCustomerGuaranteeDaysFilled(customerGuaranteeDaysByCompanySourceID, companySourceIDs)
        ) {
            setCustomerGuaranteeDaysError(true);
            setNotification({
                text: 'Customer guarantee days are required for every company source',
                level: 'error',
            });
            return false;
        }
        setCustomerGuaranteeDaysError(false);

        let extSku;
        if (!externalSku) {
            extSku = externalSkuInputValue;
        } else {
            extSku = externalSku.value.externalSku;
        }

        const skipCreate = shouldSkipItemMasterCreate({ pendingValuePropAttach });
        let createdExternalSkuFromResponse = null;

        if (!skipCreate) {
            const sendParams = {
                externalSku: extSku,
                companySourceIDs,
                itemNameInternal: formData.itemNameInternal,
                purchasingCasePackSize: purchCasePackSize,
                purchasingCaseUOM: formData.purchasingCaseUOM,
                baseUnitSize: formData.baseUnitSize,
                baseUnitUOM: formData.baseUnitUOM,
                packagingUnitSize: formData.packagingUnitSize,
                packagingUnitType: formData.packagingUnitType,
                multipackIndividualUnitSize: formData.multipackIndividualUnitSize,
                multipackIndividualUOM: formData.multipackIndividualUOM,
                baseUnitsPerCase: formData.baseUnitsPerCase,
                cubeLevelPerBaseUnit: formData.cubeLevelPerBaseUnit,
                allergen: formData.allergen === 'Yes',
                expireLifeDays: formData.expireLifeDays,
                daysAfterThaw: formData.storageTempZone === 'Frozen (0-31)' ? formData.daysAfterThaw : null,
                daysAfterOpening: formData.daysAfterOpening === 'Frozen (0-31)' ? formData.daysAfterOpening : null,
                receivingTempZone: formData.receivingTempZone,
                storageTempZone: formData.storageTempZone,
                shippingTempZone: formData.shippingTempZone,
                dbiShippingTempZone: formData.dbiShippingTempZone,
                inboundShippingTempZone: formData.inboundShippingTempZone,
                csPallet,
                lbCs: formData.lbCs,
                lengthInCs: formData.lengthInCs,
                widthInCs: formData.widthInCs,
                heightInCs: formData.heightInCs,
                ozUnit: Math.round(formData.ozUnit * 100) / 100,
                lengthInUnit: formData.lengthInUnit,
                widthInUnit: formData.widthInUnit,
                heightInUnit: formData.heightInUnit,
                l1TaxonomyID: formData.l1TaxonomyID,
                l2TaxonomyID: formData.l2TaxonomyID,
                l3TaxonomyID: formData.l3TaxonomyID,
                l4TaxonomyID: formData.l4TaxonomyID,
                tier: formData.tier,
                pricePositioning: formData.pricePositioning,
                innovation: formData.innovation === 'Yes',
                portionCountMischief: formData.portionCountMischief,
                barcode: formData.barcode,
                barcodeType,
                expirationType: formData.expirationType,
                msrpPrice: formData.msrpPrice,
                prop65Warning: formData.prop65Warning,
                prop65WarningCategoryAlcohol: formData.prop65WarningCategoryAlcohol,
                prop65WarningCategoryCanned: formData.prop65WarningCategoryCanned,
                prop65WarningCategoryFood: formData.prop65WarningCategoryFood,
                prop65WarningCategoryNonFood: formData.prop65WarningCategoryNonFood,
                prop65Ingredient: formData.prop65Ingredient,
                brand: formData.brand,
                segment: formData.segment,
                productionMethod: formData.productionMethod,
                dbiOnly: formData.dbiOnly === 'Yes',
                isBulkItem: formData.isBulkItem === 'Yes',
                replenishFacilities: facilities
                    .map((item) => {
                        if (formData.replenishFacilities.map((row) => row.text).includes(item.name)) {
                            return item.id;
                        }
                    })
                    .filter((item) => item !== undefined),
                sellOnPoQty: sellOnPo === 'Yes',
                hi: formData.hi,
                ti: formData.ti,
                vendorGuaranteedDaysUponDelivery: formData.vendorGuaranteedDaysUponDelivery,
                ...(shouldSubmitCgd
                    ? {
                          customerGuaranteeDaysByCompanySource: buildCustomerGuaranteeDaysByCompanySourcePayload(
                              customerGuaranteeDaysByCompanySourceID,
                              companySourceIDs,
                          ),
                      }
                    : {}),
            };

            const response = await api.createItemMaster(sendParams);
            if (response.status !== true) {
                let message = 'Error creating item master';
                if (response) {
                    message += `: ${response.msg}`;
                }
                setNotification({ text: message, level: 'error' });
                return shouldCompleteCreateItemMasterSubmit({
                    itemMasterCreated: false,
                    valuePropsAttachAttempted: false,
                    valuePropsAttachSucceeded: false,
                });
            }
            createdExternalSkuFromResponse = response.data?.externalSku;
        }

        const selectedValuePropIDs = getSelectedValuePropIDs(selectedValueProps);
        const valuePropIDsToAttach = getValuePropIDsToAttach({
            selectedValuePropIDs,
            pendingValuePropAttach,
        });
        const canAttachValueProps = shouldAttachValuePropsAfterCreate({
            isExistingItemInformation: Boolean(externalSku?.value),
        });
        const valuePropsAttachAttempted = canAttachValueProps && valuePropIDsToAttach.length > 0;
        let valuePropsAttachSucceeded = !valuePropsAttachAttempted;

        if (valuePropsAttachAttempted) {
            const knownExternalSkus = items
                .map((item) => Number(item?.value?.externalSku))
                .filter((sku) => Number.isFinite(sku) && sku > 0);
            let resolvedExternalSku =
                getPendingAttachExternalSku(pendingValuePropAttach) ??
                resolveExternalSkuForValueProps({
                    sentExternalSku: extSku,
                    createdExternalSku: createdExternalSkuFromResponse,
                    itemNameInternal: formData.itemNameInternal,
                    knownExternalSkus,
                    itemInformationRows: [],
                });

            if (resolvedExternalSku == null) {
                const itemInformationRes = await api.getItemInformation(true, 'itemMaster');
                if (itemInformationRes.status === true) {
                    resolvedExternalSku = resolveExternalSkuForValueProps({
                        sentExternalSku: extSku,
                        createdExternalSku: createdExternalSkuFromResponse,
                        itemNameInternal: formData.itemNameInternal,
                        knownExternalSkus,
                        itemInformationRows: itemInformationRes.data?.items ?? [],
                    });
                }
            }

            if (resolvedExternalSku == null) {
                // Create already persisted — remember SKU + IDs so retry only re-attempts attach.
                setPendingValuePropAttach({
                    attemptedValuePropIDs: valuePropIDsToAttach,
                    externalSku: null,
                    failedValuePropIDs: valuePropIDsToAttach,
                    savedValuePropIDs: getSavedValuePropIDs({
                        attemptedValuePropIDs: valuePropIDsToAttach,
                        failedValuePropIDs: valuePropIDsToAttach,
                        savedValuePropIDs: pendingValuePropAttach?.savedValuePropIDs,
                    }),
                });
                setNotification({
                    text: 'Item Master created, but value props could not be attached (external SKU not found).',
                    level: 'error',
                });
                return shouldCompleteCreateItemMasterSubmit({
                    itemMasterCreated: true,
                    valuePropsAttachAttempted: true,
                    valuePropsAttachSucceeded: false,
                });
            }

            const valuePropResponses = await Promise.all(
                valuePropIDsToAttach.map((valuePropID) =>
                    valuePropsApi.addValuePropToExternalSku(resolvedExternalSku, valuePropID),
                ),
            );
            const failedValuePropIDs = getFailedValuePropIDs({
                attemptedValuePropIDs: valuePropIDsToAttach,
                responses: valuePropResponses,
            });
            if (failedValuePropIDs.length > 0) {
                // Create already persisted — remember SKU + failed IDs so retry skips saved mappings.
                setPendingValuePropAttach({
                    attemptedValuePropIDs: valuePropIDsToAttach,
                    externalSku: resolvedExternalSku,
                    failedValuePropIDs,
                    savedValuePropIDs: getSavedValuePropIDs({
                        attemptedValuePropIDs: valuePropIDsToAttach,
                        failedValuePropIDs,
                        savedValuePropIDs: pendingValuePropAttach?.savedValuePropIDs,
                    }),
                });
                const failedCount = failedValuePropIDs.length;
                setNotification({
                    text: `Item Master created, but ${failedCount} value prop${failedCount === 1 ? '' : 's'} failed to attach.`,
                    level: 'error',
                });
                return shouldCompleteCreateItemMasterSubmit({
                    itemMasterCreated: true,
                    valuePropsAttachAttempted: true,
                    valuePropsAttachSucceeded: false,
                });
            }

            valuePropsAttachSucceeded = true;
        }

        setPendingValuePropAttach(null);
        setNotification({ text: 'Item Master created!', level: 'success' });
        return shouldCompleteCreateItemMasterSubmit({
            itemMasterCreated: true,
            valuePropsAttachAttempted,
            valuePropsAttachSucceeded,
        });
    };

    return (
        <div>
            <h1>Create Item Master</h1>
            <Notifications options={notification} />
            {items.length > 0 && (
                <div style={{ outline: 0, backgroundColor: 'white' }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12}>
                            <h3 style={{ textAlign: 'left' }}>Naming/Taxonomy</h3>
                        </Grid>
                        <Grid item xs={5}>
                            <Autocomplete
                                id="CompanySource_autocomplete"
                                multiple
                                disabled={!!externalSku?.value}
                                options={companySourceOptions}
                                getOptionLabel={(option) => option.displayName ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                value={companySourceOptions.filter((source) => companySourceIDs.includes(source.id))}
                                onChange={(event, selected) => {
                                    updateCompanySourceSelection(selected.map((source) => source.id));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Company Source"
                                        required
                                        error={companySourceError}
                                        helperText={
                                            companySourceError ? 'At least one company source is required' : undefined
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Autocomplete
                                id="ExternalSku_autocomplete"
                                key={autocompleteKey}
                                onBlur={() => {
                                    if (externalSku && externalSku.value) {
                                        return;
                                    }
                                    if (externalSkuInputValue) {
                                        // check if this is an existing sku that has not been selected from the options
                                        const item = items.find(
                                            (item) => item.value.externalSku === Number(externalSkuInputValue),
                                        );
                                        if (item) {
                                            setExternalSku(item);
                                        }
                                    }
                                }}
                                disabled={!!externalSku && !!externalSku.value}
                                value={externalSku}
                                onChange={(event, newValue) => {
                                    if (newValue && newValue.inputValue) {
                                        setExternalSku({
                                            text: newValue.inputValue,
                                        });

                                        return;
                                    }

                                    if (!newValue) {
                                        updateCompanySourceSelection(getDefaultCompanySourceIDs(companySourceOptions), {
                                            resetValues: true,
                                        });
                                        setSelectedValueProps([]);
                                    }

                                    setExternalSku(newValue);
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    setExternalSkuInputValue(params.inputValue);

                                    return filtered;
                                }}
                                selectOnFocus
                                options={items}
                                freeSolo
                                getOptionLabel={(option) => {
                                    // Value selected with enter, right from the input
                                    if (typeof option === 'string') {
                                        return option;
                                    }
                                    // Add "xxx" option created dynamically
                                    if (option.inputValue) {
                                        return option.inputValue;
                                    }
                                    // Regular option
                                    return option.text;
                                }}
                                renderOption={(option) => option.text}
                                renderInput={(params) => <TextField {...params} label="External Sku" />}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            {!!externalSku && !!externalSku.value && (
                                <Button
                                    onClick={() => {
                                        setExternalSku('');
                                        updateCompanySourceSelection(getDefaultCompanySourceIDs(companySourceOptions), {
                                            resetValues: true,
                                        });
                                        setSelectedValueProps([]);
                                    }}
                                >
                                    CLEAR
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <FormComponent
                        formFields={formFieldsRearranged}
                        onSubmit={async (formData, resetForm) => {
                            const res = await createItemMaster(formData);
                            if (res === true) {
                                setAutocompleteKey(autocompleteKey + 1);
                                resetForm();
                                setExternalSku('');
                                setExternalSkuInputValue('');
                                setPendingValuePropAttach(null);
                                updateCompanySourceSelection(getDefaultCompanySourceIDs(companySourceOptions), {
                                    resetValues: true,
                                });
                                setSelectedValueProps([]);
                                setBarcodeType('');
                                setRecTempZone('');
                                setStorTempZone('');
                                setShipTempZone('');
                                setSellOnPo('');
                                setBaseUnitsPerCase();
                                setPurchCasePackSize();
                                setCsPallet();
                                if (onSuccess && onSuccess instanceof Function) {
                                    onSuccess();
                                } else {
                                    getItemMasters();
                                }
                            }
                        }}
                    />
                    {error && <div style={{ color: 'red' }}>{error}</div>}
                </div>
            )}
        </div>
    );
}

export { CreateItemMaster };
